import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import Spinner from "../../components/Spinner";
import useAuthFetch from "../../hooks/useAuthFetch";
import { useSelector } from "react-redux";
import Pagination from "../../components/Pagination";

const PAGE_SIZE = 20;

export const BodegaProductoHistorial = () => {
  const { idBodega, idProducto } = useParams();
  const [historial, setHistorial] = useState([]);
  const [producto, setProducto] = useState(null);
  const [bodega, setBodega] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const { token } = useSelector((store) => store.app.user);
  const authFetch = useAuthFetch();

  const fetchHistorial = useCallback(async () => {
    setLoading(true);
    try {
      const historialResponse = await authFetch(
        `bodega-productos/bodega/${idBodega}/producto/${idProducto}/historial`,
        null,
        "GET",
        token
      );
      const historialData = await historialResponse.json();
      setHistorial(historialData);

      // Obtener información del producto con relaciones
      const productoResponse = await authFetch(
        `bodega-productos/bodega/${idBodega}/producto/${idProducto}?filter=${encodeURIComponent(JSON.stringify({
          include: [{
            relation: "producto",
            scope: {
              include: [{"relation": "proveedor"}, {"relation": "marca"}]
            }
          }]
        }))}`,
        null,
        "GET",
        token
      );
      const productoData = await productoResponse.json();
      setProducto(productoData);

      // Obtener información de la bodega
      const bodegaResponse = await authFetch(
        `bodegas/${idBodega}`,
        null,
        "GET",
        token
      );
      const bodegaData = await bodegaResponse.json();
      setBodega(bodegaData);
    } catch (error) {
      console.error("Error fetching historial:", error);
    } finally {
      setLoading(false);
    }
  }, [idBodega, idProducto, token, authFetch]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPage(0); // Resetear a la primera página cuando cambien los parámetros
    fetchHistorial();
  }, [fetchHistorial]);

  const formatFecha = (fechaISO) => {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleString("es-CR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case "VENTA":
        return "bg-pink text-white";
      case "ORDEN_COMPRA":
        return "bg-green-500 text-white";
      case "TRANSFERENCIA_SALIDA":
        return "bg-orange-500 text-white";
      case "TRANSFERENCIA_ENTRADA":
        return "bg-blue-500 text-white";
      case "TRANSFERENCIA_COMPLETADA":
        return "bg-indigo-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case "VENTA":
        return "Venta";
      case "ORDEN_COMPRA":
        return "Orden de Compra";
      case "TRANSFERENCIA_SALIDA":
        return "Transferencia Salida";
      case "TRANSFERENCIA_ENTRADA":
        return "Transferencia Entrada";
      case "TRANSFERENCIA_COMPLETADA":
        return "Transferencia Completada";
      default:
        return tipo;
    }
  };

  const getCantidadDisplay = (movimiento) => {
    const cantidad = movimiento.cantidad;
    if (movimiento.tipo === "VENTA" || movimiento.tipo === "TRANSFERENCIA_SALIDA") {
      return `-${cantidad}`;
    }
    if (movimiento.tipo === "TRANSFERENCIA_COMPLETADA" && movimiento.idVenta) {
      // Transferencia completada con venta: sale del sistema
      return `-${cantidad}`;
    }
    return `+${cantidad}`;
  };

  // Calcular paginación
  const totalPages = Math.ceil(historial.length / PAGE_SIZE);
  const startIndex = page * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const historialPaginado = historial.slice(startIndex, endIndex);

  return (
    <div>
      {loading && <Spinner />}
      <div className="w-full p-content pb-16">
        <div className="flex w-full items-center mb-6">
          <div className="w-full flex justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">
                Historial de Movimientos
              </h1>
              {bodega && (
                <p className="text-lg text-gray-600 mb-1">{bodega.nombre}</p>
              )}
            </div>
            <Link to={`/bodegas/inventario/${idBodega}`}>
              <button className="bg-pink text-white py-2 px-8 font-bold rounded-full flex justify-center items-center">
                <IoMdArrowRoundBack className="text-lg mr-2" /> Volver
              </button>
            </Link>
          </div>
        </div>

        {/* Información del producto arriba de la tabla */}
        {producto && (
          <div className="mb-6 flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            {producto.producto?.fotoPrincipal ? (
              <img
                src={producto.producto.fotoPrincipal}
                alt={producto.producto.nombreTienda || `Producto #${idProducto}`}
                className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-24 h-24 bg-gray-200 rounded-lg border border-gray-300 flex items-center justify-center">
                <span className="text-gray-400 text-xs">Sin imagen</span>
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                {producto.producto?.nombreTienda || `Producto #${idProducto}`}
              </h2>
              {producto.producto?.marca?.nombreTienda ? (
                <p className="text-sm text-gray-600">Marca: {producto.producto.marca.nombreTienda}</p>
              ) : producto.producto?.marca ? (
                <p className="text-sm text-gray-600">Marca: {producto.producto.marca}</p>
              ) : null}
            </div>
          </div>
        )}

        {historial.length > 0 ? (
          <div className="w-full">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-purple text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        En Bodega
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        En Tránsito
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Movimiento
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Referencia
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {historialPaginado.map((movimiento, index) => (
                      <tr
                        key={index}
                        className={`border-b ${index % 2 === 0 ? "bg-gray-50" : "bg-white"
                          } hover:bg-gray-100 transition-colors`}
                      >
                        <td className="px-4 py-3 text-sm">
                          {formatFecha(movimiento.fecha)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getTipoColor(
                              movimiento.tipo
                            )}`}
                          >
                            {getTipoLabel(movimiento.tipo)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm">
                          <div className="flex flex-col items-center">
                            <span className="text-gray-500 text-xs">
                              {movimiento.cantidadEnBodegaAntes}
                            </span>
                            <span className="mx-2">→</span>
                            <span className="font-semibold">
                              {movimiento.cantidadEnBodegaDespues}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm">
                          <div className="flex flex-col items-center">
                            <span className="text-gray-500 text-xs">
                              {movimiento.cantidadEnTransitoAntes}
                            </span>
                            <span className="mx-2">→</span>
                            <span className="font-semibold">
                              {movimiento.cantidadEnTransitoDespues}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`font-bold text-lg ${
                              movimiento.tipo === "VENTA" ||
                              movimiento.tipo === "TRANSFERENCIA_SALIDA" ||
                              (movimiento.tipo === "TRANSFERENCIA_COMPLETADA" && movimiento.idVenta)
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                          >
                            {getCantidadDisplay(movimiento)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-1">
                            {movimiento.idVenta && (
                              <Link
                                to={`/pedidos/${movimiento.idVenta}`}
                                className="text-cyan hover:underline"
                              >
                                Venta #{movimiento.idVenta}
                              </Link>
                            )}
                            {movimiento.idOrdenCompra && (
                              <Link
                                to={`/ordenes/ver/${movimiento.idOrdenCompra}`}
                                className="text-cyan hover:underline"
                              >
                                Orden #{movimiento.idOrdenCompra}
                              </Link>
                            )}
                            {movimiento.idTransferencia && (
                              <span className="text-gray-600">
                                Transferencia #{movimiento.idTransferencia}
                              </span>
                            )}
                            {!movimiento.idVenta &&
                              !movimiento.idOrdenCompra &&
                              !movimiento.idTransferencia && (
                                <span className="text-gray-400">-</span>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(newPage) => {
                  setPage(newPage);
                  window.scrollTo(0, 0);
                }}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-xl text-gray-500">
              No hay movimientos registrados para este producto
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BodegaProductoHistorial;
